import {
  SubscriptionCheckoutLineItemDto,
  SubscriptionCheckoutPaymentMethodDto,
  SubscriptionCheckoutResponseDto,
  SubscriptionInterval,
} from '../api';
import { normalizeSubscriptionPlanCurrency } from './subscriptionPlanPricing';

export interface SubscriptionCheckoutPricingSnapshot {
  orderId?: string;
  orderNumber?: string;
  planCode?: string;
  planName?: string;
  planDescription?: string;
  interval?: SubscriptionInterval;
  intervalCount: number;
  monthlyCreditsIncluded: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discount: number;
  total: number;
  currency: string;
  hasTax: boolean;
  hasDiscount: boolean;
  selectedPaymentMethodCode?: string;
  selectedPaymentProviderCode?: string;
  availablePaymentMethods: SubscriptionCheckoutPaymentMethodDto[];
  lines: SubscriptionCheckoutLineItemDto[];
}

export const buildSubscriptionCheckoutPricingSnapshot = (
  checkout?: SubscriptionCheckoutResponseDto | null,
): SubscriptionCheckoutPricingSnapshot => {
  const orderId = checkout?.orderId;
  const orderNumber = checkout?.orderNumber;
  const planCode = checkout?.planCode;
  const planName = checkout?.planName;
  const planDescription = checkout?.planDescription;
  const interval = checkout?.interval;
  const intervalCount = checkout?.intervalCount ?? 1;
  const monthlyCreditsIncluded = checkout?.monthlyCreditsIncluded ?? 0;
  const subtotal = checkout?.subtotalAmount ?? 0;
  const taxRate = checkout?.taxRate ?? 0;
  const taxAmount = checkout?.taxAmount ?? 0;
  const discount = checkout?.discountAmount ?? 0;
  const total = checkout?.totalAmount ?? subtotal + taxAmount - discount;
  const currency = normalizeSubscriptionPlanCurrency(checkout?.currency);
  const hasTax = taxAmount > 0;
  const hasDiscount = discount > 0;
  const selectedPaymentMethodCode = checkout?.selectedPaymentMethodCode;
  const selectedPaymentProviderCode = checkout?.selectedPaymentProviderCode;
  const availablePaymentMethods =
    checkout?.availablePaymentMethods?.filter(
      (method): method is SubscriptionCheckoutPaymentMethodDto => Boolean(method),
    ) ?? [];
  const lines =
    checkout?.lines?.filter((line): line is SubscriptionCheckoutLineItemDto => Boolean(line)) ?? [];

  return {
    orderId,
    orderNumber,
    planCode,
    planName,
    planDescription,
    interval,
    intervalCount,
    monthlyCreditsIncluded,
    subtotal,
    taxRate,
    taxAmount,
    discount,
    total,
    currency,
    hasTax,
    hasDiscount,
    selectedPaymentMethodCode,
    selectedPaymentProviderCode,
    availablePaymentMethods,
    lines,
  };
};

export const STRIPE_CARD_METHOD_CODE = 'CARD';
export const STRIPE_CARD_PROVIDER_CODE = 'stripe';

export const findSubscriptionCheckoutPaymentMethod = (
  snapshot: SubscriptionCheckoutPricingSnapshot,
  methodCode: string,
  providerCode: string,
): SubscriptionCheckoutPaymentMethodDto | undefined =>
  snapshot.availablePaymentMethods.find(
    (method) =>
      (method.methodCode ?? '').toUpperCase() === methodCode.toUpperCase() &&
      (method.providerCode ?? '').toLowerCase() === providerCode.toLowerCase(),
  );

// The Stripe card option is surfaced only when the backend checkout snapshot returns the CARD/stripe pair.
export const getStripeCardCheckoutPaymentMethod = (
  snapshot: SubscriptionCheckoutPricingSnapshot,
): SubscriptionCheckoutPaymentMethodDto | undefined =>
  findSubscriptionCheckoutPaymentMethod(snapshot, STRIPE_CARD_METHOD_CODE, STRIPE_CARD_PROVIDER_CODE);

export const hasStripeCardCheckoutOption = (snapshot: SubscriptionCheckoutPricingSnapshot): boolean =>
  Boolean(getStripeCardCheckoutPaymentMethod(snapshot));
